import { useEffect, useState } from "react";
import {
  Button,
  Card,
  Divider,
  FileButton,
  Group,
  Image,
  Stack,
  Switch,
  Text,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AnalyticsConfig } from "../../../api.ts";
import { adminGet, adminSend, adminUpload } from "../../../api.ts";
import { notifyAdminError, notifyAdminSuccess } from "../../../lib/admin-notify.ts";

/** 上报配置 + 落地页 bot 默认（放「基础设置」；频道覆盖仍在「频道/群聊」） */

export function ReportConfigCard(props: { readonly botId: string }) {
  const { botId } = props;
  const qc = useQueryClient();

  const defaultEvents: AnalyticsConfig["events"] = {
    visit: { enabled: false, eventName: null },
    click: { enabled: false, eventName: null },
    join: { enabled: true, eventName: null },
    joinUnique: { enabled: false, eventName: null },
    leave: { enabled: false, eventName: null },
  };

  const [values, setValues] = useState<AnalyticsConfig>({
    events: defaultEvents,
    dedupEnabled: true,
    reportOnLeave: false,
    defaultEventName: null,
  });

  const configQuery = useQuery({
    queryKey: ["bot", botId, "analytics-config"],
    queryFn: () => adminGet<AnalyticsConfig>(`/analytics/${botId}/config`),
  });

  useEffect(() => {
    const d = configQuery.data;
    if (d != null) {
      setValues({
        events: d.events ?? defaultEvents,
        dedupEnabled: d.dedupEnabled ?? true,
        reportOnLeave: d.reportOnLeave ?? false,
        defaultEventName: d.defaultEventName ?? null,
      });
    }
  }, [configQuery.data]);

  const saveMutation = useMutation({
    mutationFn: (v: AnalyticsConfig) =>
      adminSend("PUT", `/analytics/${botId}/config`, {
        dedupEnabled: v.dedupEnabled,
        reportOnLeave: v.reportOnLeave,
        defaultEventName: v.defaultEventName === "" ? null : (v.defaultEventName ?? null),
      }),
    onSuccess: () => {
      notifyAdminSuccess("已保存上报配置");
      void qc.invalidateQueries({ queryKey: ["bot", botId, "analytics-config"] });
    },
    onError: (e) => notifyAdminError(e, "保存失败"),
  });

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Text fw={600} size="lg" mb="md">
        上报配置
      </Text>
      <Stack gap="md">
        <TextInput
          label="默认事件名"
          placeholder="如 CompleteRegistration、Subscribe、Purchase"
          description="广告未单独配置事件名时，转化上报默认用这个；广告级事件名在「广告列表」单独配"
          value={values.defaultEventName ?? ""}
          onChange={(e) => {
            const v = e.currentTarget.value;
            setValues((prev) => ({ ...prev, defaultEventName: v }));
          }}
        />
        <Divider />
        <Switch
          label="同用户同广告去重（防重复上报）"
          checked={values.dedupEnabled}
          onChange={(e) => {
            const checked = e.currentTarget.checked;
            setValues((prev) => ({ ...prev, dedupEnabled: checked }));
          }}
        />
        <Switch
          label="退群写入转化记录（默认关闭）"
          description="开启后退群按广告归属写进转化记录，只用于统计与对账，不上报给任何广告平台"
          checked={values.reportOnLeave}
          onChange={(e) => {
            const checked = e.currentTarget.checked;
            setValues((prev) => ({ ...prev, reportOnLeave: checked }));
          }}
        />
        <Group justify="flex-end">
          <Button loading={saveMutation.isPending} onClick={() => saveMutation.mutate(values)}>
            保存
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

export function LandingBgUploader(props: {
  readonly label: string;
  readonly hint: string;
  readonly value: string | null;
  readonly onChange: (url: string | null) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const handleFile = (file: File | null) => {
    if (file === null) return;
    setUploading(true);
    adminUpload(file)
      .then((r) => props.onChange(r.url))
      .catch(() => notifications.show({ color: "red", message: "上传失败，请重试" }))
      .finally(() => setUploading(false));
  };
  return (
    <Stack gap={6}>
      <Text size="sm" fw={500}>
        {props.label}
      </Text>
      <Text size="xs" c="dimmed">
        {props.hint}
      </Text>
      {props.value !== null && (
        <Image
          src={props.value}
          alt=""
          h={120}
          w="auto"
          fit="contain"
          radius="sm"
          style={{ alignSelf: "flex-start" }}
        />
      )}
      <Group gap="xs">
        <FileButton onChange={handleFile} accept="image/png,image/jpeg,image/webp">
          {(btnProps) => (
            <Button {...btnProps} size="compact-sm" variant="light" loading={uploading}>
              {props.value !== null ? "替换图片" : "上传图片"}
            </Button>
          )}
        </FileButton>
        {props.value !== null && (
          <Button
            size="compact-sm"
            variant="subtle"
            color="red"
            onClick={() => props.onChange(null)}
          >
            清除
          </Button>
        )}
      </Group>
    </Stack>
  );
}

export const LANDING_CARD_BG_HINT =
  "建议 900x1200（3:4 竖图）、不超过 300KB；移动端 cover 居中裁切";
export const LANDING_PAGE_BG_HINT =
  "建议 1080x1920（9:16 竖图）、不超过 500KB；移动端优先，关键内容放画面中央";

export function LandingBgCard(props: { readonly botId: string }) {
  const { botId } = props;
  const qc = useQueryClient();
  const [cardBg, setCardBg] = useState<string | null>(null);
  const [pageBg, setPageBg] = useState<string | null>(null);
  // 只用首次加载的数据初始化：refetch 不得覆盖未保存的本地编辑
  const [initialized, setInitialized] = useState(false);

  const configQuery = useQuery({
    queryKey: ["bot", botId, "analytics-config"],
    queryFn: () => adminGet<AnalyticsConfig>(`/analytics/${botId}/config`),
  });

  useEffect(() => {
    const d = configQuery.data;
    if (d != null && !initialized) {
      setCardBg(d.landingCardBgUrl ?? null);
      setPageBg(d.landingPageBgUrl ?? null);
      setInitialized(true);
    }
  }, [configQuery.data, initialized]);

  const save = useMutation({
    mutationFn: () =>
      adminSend("PUT", `/analytics/${botId}/landing-bg`, { cardBgUrl: cardBg, pageBgUrl: pageBg }),
    onSuccess: () => {
      notifyAdminSuccess("已保存落地页背景");
      void qc.invalidateQueries({ queryKey: ["bot", botId, "analytics-config"] });
    },
    onError: (e) => notifyAdminError(e, "保存失败"),
  });

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Text fw={600} size="lg" mb={4}>
        落地页背景（bot 默认）
      </Text>
      <Text size="xs" c="dimmed" mb="md">
        Google 落地页外观默认图。单个频道/群可在「频道/群聊 -&gt; 更多 -&gt; 落地页背景」单独覆盖。
        图片上会叠半透明底色保证文字可读，建议浅色淡纹理图。
      </Text>
      <Stack gap="md">
        <LandingBgUploader
          label="卡片背景（替换白色卡片底）"
          hint={LANDING_CARD_BG_HINT}
          value={cardBg}
          onChange={setCardBg}
        />
        <Divider />
        <LandingBgUploader
          label="页面背景（全屏）"
          hint={LANDING_PAGE_BG_HINT}
          value={pageBg}
          onChange={setPageBg}
        />
        <Group justify="flex-end">
          <Button loading={save.isPending} onClick={() => save.mutate()}>
            保存
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}
